'use client'
import { useEffect, useState, useCallback, useRef } from 'react'
import Step2 from './step-2-payment'
import Step3 from './step-3-confirm'
import { Check, X } from 'lucide-react'
import BreadcrumbMobile from '../ui/breadcrumb-mobile'
import Step1 from './step-1-booking'
import OverlayLoading from '../ui/overlay-loading'
import Message, { type MessageType } from '../ui/message'
import PaymentModal from './payment-modal'
import { usePaymentData } from '~/hooks/use-payment-data'
import { useDevice } from '~/hooks/use-device'
import IcBack from '~/icons/ic_back'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { useCancelBooking } from '~/hooks/use-booking'
import { BookingStep, useBookingFlow, type PaymentStep } from '~/stores/booking-flow'
import { useSignalR } from '~/hooks/use-signalr'
import TimeoutDeadline from '~/components/payment/timeout-deadline'
import { useSearchParams } from 'next/navigation'
import TimeoutModal from './timeout-modal'
import RoomAvailability from './room-availability'

const StepIndicatorSteps = (t) => [t('steps.booking'), t('steps.payment'), t('steps.confirm')]

const WEB_SOCKET = process.env.NEXT_PUBLIC_CRS_URL

interface Props {
  hotelId: string
  arrivalDate: string
  departureDate: string
  onBackToSearch: () => void
  timeIn: string
  timeOut: string
  nights: number
  totalRooms: number
  totalAmount: number
  totalChild?: number
  totalAdult?: number
}

interface SocketData {
  // Add socket data type here
}

export interface PaymentData {
  bookingNo: string
  customerName: string
  fullName: string
  cardItems: Array<{
    roomId: string
    roomName: string
    amount: number
    guests: {
      adults: number
      childrenUnder6: number
      children6To12: number
    }
  }>
  arrivalDate: string
  departureDate: string
  totalAmount: number
  cancelTime: number
}

export default function StepIndicator({
  hotelId,
  arrivalDate,
  departureDate,
  onBackToSearch,
  timeIn,
  timeOut,
  nights,
  totalRooms,
  totalAmount,
}: Props) {
  const [isLoading, setIsLoading] = useState(true)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const [title, setTitle] = useState(t('steps.customerInfo'))
  const [dataSocket, setDataSocket] = useState<SocketData | null>(null)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<MessageType>('error')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const { isMobile } = useDevice()
  const { currentPaymentStep, setCurrentPaymentStep, setCurrentStep } = useBookingFlow()
  const [paymentResults, setPaymentResults] = useState('')
  const [isOutTimePayment, setIsOutTimePayment] = useState(false)
  const [isRoomAvailability, setIsRoomAvailability] = useState(false)

  const { paymentData } = usePaymentData()
  const { cancelBooking } = useCancelBooking()
  const handleSocketMessage = useCallback(
    (data) => {
      console.log('data socket', data)
      if (currentPaymentStep === 2) {
        if (data && Object.keys(data).length > 0) {
          setDataSocket(data)
          nextStep(data)
        }
      }
    },
    [currentPaymentStep]
  )

  const handleSocketMessageRoom = useCallback(
    (data) => {
      console.log('🚀 ~ StepIndicator ~ data room:', data)
      if (currentPaymentStep === 1) {
        if (data && Object.keys(data).length > 0) {
          console.log('🚀 ~ StepIndicator ~ data:', data)
          setIsRoomAvailability(true)
        }
      }
    },
    [currentPaymentStep]
  )

  const handleSocketError = useCallback(
    (error) => {
      console.log('🚀 ~ StepIndicator ~ error:', error)
      // setMessage(t('booking.connectionError'))
      // setMessageType('error')
    },
    [t]
  )
  const searchParams = useSearchParams()
  const bookingNo = searchParams.get('bookingNo')
  const { sendMessage } = useSignalR(paymentData?.bookingNo || bookingNo || '', {
    onMessage: handleSocketMessage,
    onError: handleSocketError,
  })

  useEffect(() => {
    if (paymentData?.bookingNo && currentPaymentStep === 2) {
      if (paymentData.bookingNo || bookingNo) {
        sendMessage('Register', paymentData.bookingNo || bookingNo || '')
      }
    }
  }, [paymentData?.bookingNo])

  const { sendMessage: sendMessageSignalR } = useSignalR('MHG', {
    onMessage: handleSocketMessageRoom,
    onError: handleSocketError,
  })

  useEffect(() => {
    if (currentPaymentStep === 1) {
      sendMessageSignalR('CheckRoomAvailable', 'MHG')
    }
  }, [currentPaymentStep])

  useEffect(() => {
    setIsLoading(true)
    const queryParams = new URLSearchParams(window.location.search)
    const stepActive = queryParams.get('step')
    const result = queryParams.get('paymentResults')

    if (result) {
      setPaymentResults(result)
    }

    if (stepActive) {
      const step = Number(stepActive)
      if (step >= 1 && step <= 3) {
        setCurrentPaymentStep(step as PaymentStep)
      }
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (currentPaymentStep === 1) {
      setTitle(t('steps.customerInfo'))
    } else {
      setTitle(t('steps.payment'))
    }

    if (currentPaymentStep === 3 && !isMobile) {
      setIsOpenModal(true)
    }
  }, [currentPaymentStep])

  // Store search URL for back navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    // Replace current state with search params first
    window.history.replaceState(
      {
        isSearchPage: true,
        searchParams: searchParams.toString(),
      },
      '',
      `${window.location.pathname}?${searchParams}`
    )

    // Then push payment state
    window.history.pushState(
      {
        paymentStep: 1,
        isPaymentFlow: true,
        searchParams: searchParams.toString(),
      },
      '',
      window.location.href
    )
  }, []) // Empty deps array as this should only run once on mount

  // Track step changes and update history
  useEffect(() => {
    if (currentPaymentStep === 2) {
      window.history.pushState(
        {
          paymentStep: 2,
          isPaymentFlow: true,
        },
        '',
        window.location.href
      )
    }
  }, [currentPaymentStep])

  useEffect(() => {
    if (isOpenModal && !dataSocket) {
      urlStep()
      setCurrentStep(BookingStep.LIST)
      setIsOpenModal(false)
      // const params = new URLSearchParams()
      // params.set('hotelId', hotelId)
      // params.set('checkInDate', arrivalDate || '')
      // params.set('checkOutDate', departureDate || '')
      // params.set('adults', String(paymentData?.cardItems?.[0]?.guests?.adults || 1))
      // params.set('kidsOver6', String(paymentData?.cardItems?.[0]?.guests?.children6To12 || 0))
      // params.set('kidsUnder6', String(paymentData?.cardItems?.[0]?.guests?.childrenUnder6 || 0))
      // window.location.href = `${window.location.pathname}?${params}`
    }
  }, [isOpenModal])

  const urlStep = (step?: number) => {
    const params = new URLSearchParams()
    params.set('hotelId', hotelId)
    params.set('checkInDate', arrivalDate || '')
    params.set('checkOutDate', departureDate || '')
    params.set('adults', String(paymentData?.cardItems?.[0]?.guests?.adults || 1))
    params.set('kidsOver6', String(paymentData?.cardItems?.[0]?.guests?.children6To12 || 0))
    params.set('kidsUnder6', String(paymentData?.cardItems?.[0]?.guests?.childrenUnder6 || 0))
    if (step) {
      params.set('step', String(step || 1))
      params.set('epayResult', 'true')
    }

    window.history.replaceState({ isSearchPage: true }, '', `${window.location.pathname}?${params}`)
    localStorage.removeItem('PaymentDeadline')
  }

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If we're in step 1 and going back
      if (currentPaymentStep === 1) {
        urlStep()
        onBackToSearch()
        return
      }

      // If we're going back to search page
      if (event.state?.isSearchPage) {
        urlStep()
        setCurrentPaymentStep(1)
        onBackToSearch()
        return
      }

      // If we're in payment flow
      if (event.state?.isPaymentFlow) {
        if (currentPaymentStep === 2) {
          cancelBooking({
            hotelId: Number(hotelId),
            bookingId: paymentData?.bookingNo || '',
            reason: 'User cancelled the booking',
          })
          urlStep(1)
        }
        setCurrentPaymentStep(event.state.paymentStep || 1)
      } else {
        if (currentPaymentStep <= 1) {
          urlStep()
          onBackToSearch()
        } else {
          if (currentPaymentStep === 2) {
            cancelBooking({
              hotelId: Number(hotelId),
              bookingId: paymentData?.bookingNo || '',
              reason: 'User cancelled the booking',
            })
            urlStep(1)
          }
          setCurrentPaymentStep(event.state?.paymentStep || 1)
        }
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [currentPaymentStep])

  const handleBackToSearch = useCallback(() => {
    if (currentPaymentStep === 2) {
      cancelBooking({
        hotelId: Number(hotelId),
        bookingId: paymentData?.bookingNo || '',
        reason: 'User cancelled the booking',
      })
    }
    urlStep(currentPaymentStep === 2 ? 1 : undefined)
    setCurrentPaymentStep(1)
    onBackToSearch()
  }, [
    currentPaymentStep,
    hotelId,
    paymentData?.bookingNo,
    cancelBooking,
    onBackToSearch,
    arrivalDate,
    departureDate,
    paymentData,
  ])

  const nextStep = async (data: SocketData | null = null) => {
    if (currentPaymentStep === 2 && data && !Object.keys(data).length) {
      setDataSocket(null)
      return
    }
    setDataSocket(data)
    const nextStepNumber = Math.min(currentPaymentStep + 1, 3) as PaymentStep
    setCurrentPaymentStep(nextStepNumber)
    console.log('aaa', currentPaymentStep)
  }

  if (isLoading) {
    return <OverlayLoading show={true} text={t('common.loading')} />
  }

  return (
    <>
      <BreadcrumbMobile
        title={title}
        isBack={currentPaymentStep <= 2}
        onBackToSearch={handleBackToSearch}
        className="md:mt-[61px]"
      />

      <div className="flex w-full items-center justify-center border-b border-b-[#CED0D3] bg-[#fff] py-3 text-sm font-medium text-secondary-950 sm:text-base md:mt-0">
        {StepIndicatorSteps(t).map((step, index) => (
          <div
            key={index}
            className={`xs:w-full xs:justify-center relative flex items-center ${index == 0 ? 'sm:justify-end' : 'justify-center'} ${index == 2 ? 'sm:justify-start' : 'justify-center'}`}
          >
            <div className="text-[clamp(12px, 2vw, 24px)] flex items-center gap-2 whitespace-nowrap px-2">
              <span
                className={`text-[clamp(12px, 2vw, 24px)] mr-1 flex h-6 w-6 items-center justify-center rounded-full border sm:h-8 sm:w-8 ${
                  currentPaymentStep > index + 1
                    ? 'border-[#047819] bg-[#047819] text-white'
                    : currentPaymentStep === index + 1
                      ? currentPaymentStep === 3
                        ? paymentResults === 'FAIL' || isOutTimePayment
                          ? 'border-red-500 bg-red-500 text-white'
                          : 'border-[#047819] bg-[#047819] text-white'
                        : 'border-primary-500 bg-primary-500 text-white'
                      : 'border-[#EBE3D6] bg-[#EBE3D6] text-primary-500'
                }`}
              >
                {currentPaymentStep > index + 1 ||
                (paymentResults === 'SUCCESS' && currentPaymentStep === 3) ? (
                  <Check width={14} height={14} />
                ) : (paymentResults === 'FAIL' || isOutTimePayment) && currentPaymentStep === 3 ? (
                  <X width={14} height={14} />
                ) : (
                  index + 1
                )}
              </span>
              {step}
            </div>

            {index < StepIndicatorSteps(t).length - 1 && (
              <div className="h-1 w-[2vw] border-b border-[#CED0D3] sm:inline-block sm:w-24"></div>
            )}
          </div>
        ))}
      </div>
      <div className="max-w-container mx-auto hidden md:block">
        <div
          className="my-4 hidden w-fit items-center justify-start gap-2 md:mx-5 md:flex lg:mx-10"
          onClick={handleBackToSearch}
        >
          <IcBack />
          <button className="font-lexend font-medium leading-4 text-primary-500">
            {t('common.back')}
          </button>
        </div>
      </div>
      <div className="h-[1px] w-full bg-[#CED0D3] sm:hidden"></div>
      <div className="max-w-container xs:bg-white mx-auto">
        <div className="md:mx-5 lg:mx-10">
          {currentPaymentStep === 1 && (
            <Step1
              nextStep={nextStep}
              hotelId={hotelId}
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              timeIn={timeIn}
              timeOut={timeOut}
              nights={nights}
              totalRooms={totalRooms}
              totalAmount={totalAmount}
            />
          )}
          {currentPaymentStep === 2 && (
            <Step2
              nextStep={nextStep}
              dataPayment={paymentData as PaymentData}
              hotelId={hotelId}
              timeIn={timeIn}
              timeOut={timeOut}
              isTimeOut={(event) => {
                if (isMobile && event) {
                  setIsOutTimePayment(event)
                  setCurrentPaymentStep(3)
                }
              }}
            />
          )}
          {currentPaymentStep === 3 ? (
            isMobile ? (
              <>
                {!isOutTimePayment ? (
                  <Step3
                    dataSocket={dataSocket}
                    onCloseAction={() => {
                      setCurrentPaymentStep(2)
                    }}
                    transactionResults={setPaymentResults}
                  />
                ) : (
                  <TimeoutDeadline hotelId={hotelId} dataPayment={paymentData as PaymentData} />
                )}
              </>
            ) : (
              <>
                <Step2
                  nextStep={nextStep}
                  dataPayment={paymentData as PaymentData}
                  hotelId={hotelId}
                  timeIn={timeIn}
                  timeOut={timeOut}
                  isTimeOut={setIsOutTimePayment}
                  isOpenModelStep3={isOpenModal}
                />
                <div className="opacity-0">
                  <Step3
                    dataSocket={dataSocket}
                    transactionResults={setPaymentResults}
                    onCloseAction={() => {
                      setIsOpenModal(false)
                    }}
                  />
                </div>
              </>
            )
          ) : null}
        </div>
      </div>
      {!isMobile && (
        <PaymentModal isOpen={isOpenModal}>
          <Step3
            dataSocket={dataSocket}
            onCloseAction={() => {
              setIsOpenModal(false)
            }}
            transactionResults={setPaymentResults}
          />
        </PaymentModal>
      )}

      {message && <Message message={message} status={messageType} onClose={() => setMessage('')} />}
      <TimeoutModal isOpen={isRoomAvailability} onClose={() => setIsRoomAvailability(false)}>
        <RoomAvailability onClose={() => setIsRoomAvailability(false)} />
      </TimeoutModal>
    </>
  )
}
